import { computed, type ComputedRef } from 'vue';
import { amplitudeLib } from 'freedom-shared';

export const QUICK_BOOKING_FLAG = 'avfree_quick_booking_flow';

/**
 * Three-arm experiment that decides the booking entry point after an offer
 * is clicked on ResultsPage:
 *
 * - CLASSIC: results → OfferDetails → FareFamilyPage (legacy flow).
 * - AFTER_DETAILS: results → OfferDetails → QuickBookingPage. The user
 *   still sees the flight-details screen but lands in the consolidated
 *   QuickBooking page on "Continue".
 * - IMMEDIATE: results → QuickBookingPage directly. Exception: when the
 *   click originated from the direct-flights block the user still goes
 *   through OfferDetails first (so they can pick airline/time on that
 *   screen) and only then proceeds to QuickBookingPage.
 */
export const QUICK_BOOKING_VARIANT = {
  CLASSIC: 'classic_booking',
  AFTER_DETAILS: 'quick_after_details',
  IMMEDIATE: 'quick_immediate',
} as const;

export type QuickBookingVariant =
  (typeof QUICK_BOOKING_VARIANT)[keyof typeof QUICK_BOOKING_VARIANT];

interface QuickBookingExperiment {
  isExperimentLoading: ComputedRef<boolean>;
  isClassicBooking: ComputedRef<boolean>;
  isQuickAfterDetails: ComputedRef<boolean>;
  isQuickImmediate: ComputedRef<boolean>;
  /** True for any variant that funnels the user into QuickBookingPage. */
  isQuickBooking: ComputedRef<boolean>;
  variant: ComputedRef<QuickBookingVariant | null>;
}

const KNOWN_VARIANTS = new Set<string>(Object.values(QUICK_BOOKING_VARIANT));

/**
 * The shared `checkExperimentVariant` wraps the SDK in a computed that depends only on
 * the shallow `experiment` ref. The Amplitude SDK populates variants internally during
 * `client.start()` without replacing the ref, so for non-cross-app-cached experiments
 * the computed caches `null` and never re-evaluates.
 *
 * Here we read both `experiment` and `isExperimentLoading` so the computed re-runs once
 * loading flips to false (i.e. after start() resolves and variants are available).
 */
export const useQuickBookingExperiment = (): QuickBookingExperiment => {
  const { experiment, isExperimentLoading } =
    amplitudeLib.useAmplitudeExperiment();

  const variant = computed<QuickBookingVariant | null>(() => {
    if (isExperimentLoading.value) {
      return null;
    }
    const key = experiment.value?.variant?.(QUICK_BOOKING_FLAG)?.key;
    if (key && KNOWN_VARIANTS.has(key)) {
      return key as QuickBookingVariant;
    }
    return null;
  });

  return {
    isExperimentLoading: computed(() => isExperimentLoading.value),
    isClassicBooking: computed(
      () => variant.value === QUICK_BOOKING_VARIANT.CLASSIC,
    ),
    isQuickAfterDetails: computed(
      () => variant.value === QUICK_BOOKING_VARIANT.AFTER_DETAILS,
    ),
    isQuickImmediate: computed(
      () => variant.value === QUICK_BOOKING_VARIANT.IMMEDIATE,
    ),
    isQuickBooking: computed(
      () =>
        variant.value === QUICK_BOOKING_VARIANT.AFTER_DETAILS ||
        variant.value === QUICK_BOOKING_VARIANT.IMMEDIATE,
    ),
    variant,
  };
};
